import { z } from 'zod';
import type { NeedsResourceDetailsDto } from './resourceDetails.dto';
import type { ResourceDto } from './resourcing.dto';
import type { AssignedSurveyDto } from './surveys';

export interface NeedDto {
  id: number;
  creatorId: string;
  closedById: string;
  memberId: string;
  issueId: number;
  assignedSurveyId: number;
  reason: string;
  originalReason: string;
  identifiedAt: number;
  fulfilledAt: number;
  expiresAt: number;
  abandonedAt: number;
  historyEntryIds: number[];
  issueLabel?: string;
  createdAt: number;
  canDelete: boolean;

  // Standardized goals fields
  goalType: 'need';
  needRequiresAction: boolean;
  completed: boolean;
  dueDate: number;

  // Relationships
  assignedSurvey: AssignedSurveyDto;
}

export interface NeedViewDto {
  needs: NeedDto[];
  actions: NeedsActionDto[];
  history: NeedsHistoryEntryDto[];
  needUpdates: NeedUpdateDto[];
  resourceReferrals: NeedsResourceDetailsDto[];
}

export interface CreateNeedFormDto {
  domainId: number;
  categoryId: number;
  issueId: number;
  reason: string;
}

export interface NeedsActionDto {
  id: number;
  needId: number;
  creatorId: string;
  activityId: number;
  originalActionId?: number;
  date: string;
  durationInMinutes: number;
  description: string;
  createdAt: number;
  subsequentActions?: NeedsActionDto[];
  resourceReferrals?: NeedsResourceDetailsDto[];
  canDelete: boolean;
}

export enum NeedUpdateKey {
  IDENTIFIED_AT = 'identifiedAt',
  FULFILLED_AT = 'fulfilledAt',
  ABANDONED_AT = 'abandonedAt',
  EXPIRES_AT = 'expiresAt',
  REASON = 'reason',
  ISSUE = 'issue',
}

export interface NeedUpdateEntryCreateRequest {
  needId: number;
  needUpdateId?: number;
  needResourceInteractionId?: number;
  resourceDetailsId?: number;
  actionId?: number;
}

export interface NeedUpdateDto {
  id: number;
  key: NeedUpdateKey;
  siblingUpdateId: number;
  needId: number;
  creatorId: string;
  originalValue: string;
  newValue: string;
  comment?: string;
  createdAt: number;
}

export interface NeedsHistoryEntryDto {
  id: number;
  needId: number;
  actionId?: number;
  needUpdateId?: number;
  resourceDetailsId?: number;
  callId: number;
  visitId?: number;
  resource: ResourceDto; // OLD RESOURCE TYPE
  createdAt: number;
}

export interface NeedsHistoryEntryViewDto {
  needHistoryEntry: NeedsHistoryEntryDto;
  needAction?: NeedsActionDto;
  needUpdate?: NeedUpdateDto;
  resourceDetails?: NeedsResourceDetailsDto;
}

export interface NeedsActivityDto {
  id: number;
  key: string;
  label: string;
  overview: string;
  iconName: string;
  defaultTimeFrameInMinutes: number;
}

export interface ZCodeDto {
  id: number;
  label: string;
  key: string;
  overview: string;
  issues: string[];
  assistances: string[];
}

export interface NeedsDomainDto {
  id: number;
  label: string;
  key: string;
}

export interface NeedsCategoryDto {
  id: number;
  label: string;
  key: string;
  domainId: number;
}

export interface NeedsIssueDto {
  id: number;
  label: string;
  key: string;
  categoryId: number;
  defaultTimeFrameInDays: number;
  activityIds: number[];
  zcodeId: number;
  tagCloud: string;
}

export interface NeedsMetadataDto {
  activities: NeedsActivityDto[];
  categories: NeedsCategoryDto[];
  domains: NeedsDomainDto[];
  issues: NeedsIssueDto[];
  zcodes: ZCodeDto[];
}

export type MetadataDtoMap = {
  activities: NeedsActivityDto;
  categories: NeedsCategoryDto;
  domains: NeedsDomainDto;
  issues: NeedsIssueDto;
  zcodes: ZCodeDto;
};

export type MetadataType =
  | 'activities'
  | 'categories'
  | 'domains'
  | 'issues'
  | 'zcodes';

export interface CreateIssueRequest {
  label: string;
  key: string;
  categoryId: number;
  defaultTimeFrameInDays: number;
  activityIds: number[];
  zcodeId: number;
  tagCloud: string;
}

export interface UpdateIssueRequest {
  id?: number;
  label?: string;
  key?: string;
  categoryId?: number;
  defaultTimeFrameInDays?: number;
  activityIds?: number[];
  zcodeId?: number;
  tagCloud?: string;
}

export interface CreateZcodeRequest {
  label?: string;
  key: string;
  overview: string;
  issues?: string;
  assistances?: string;
}

export interface UpdateZcodeRequest {
  id?: number;
  label?: string;
  key: string;
  overview: string;
  issues?: string;
  assistances?: string;
}

export interface CreateActivityRequest {
  key: string;
  label: string;
  overview: string;
  iconName?: string;
  defaultTimeFrameInMinutes: number;
}

export interface UpdateActivityRequest {
  id?: number;
  key: string;
  label: string;
  overview: string;
  iconName?: string | null;
  defaultTimeFrameInMinutes: number;
}

export interface CreateCategoryRequest {
  label: string;
  key: string;
  domainId: number;
}

export interface UpdateCategoryRequest {
  id?: number;
  label: string;
  key: string;
  domainId: number;
}

export interface CreateDomainRequest {
  label: string;
  key: string;
}

export interface UpdateDomainRequest {
  id?: number;
  label: string;
  key: string;
}

export const IssueSchema = z.object({
  label: z.string().nonempty('required'),
  key: z
    .string()
    .nonempty('required')
    .regex(/^[^\s]+$/, 'Key cannot contain spaces'),
  categoryId: z.number(),
  defaultTimeFrameInDays: z.coerce
    .number()
    .min(0, 'Default time frame must be at least 0'),
  activityIds: z.array(z.number()),
  zcodeId: z.number(),
  tagCloud: z.string().nonempty('required'),
});

export const ZcodeSchema = z.object({
  label: z.string().optional(),
  key: z
    .string()
    .nonempty('required')
    .regex(
      /^zcode_Z\d+\.\d+$/,
      'Key must be in format "zcode_Z##.#" (e.g., zcode_Z59.82 or zcode_Z59.4)'
    ),
  overview: z.string().nonempty('required'),
  issues: z.string().optional(),
  assistances: z.string().optional(),
});

export const ActivitySchema = z.object({
  key: z.string().nonempty('required'),
  label: z.string().nonempty('required'),
  overview: z.string().nonempty('required'),
  iconName: z.string().optional(),
  defaultTimeFrameInMinutes: z.coerce.number(),
});

export const CategorySchema = z.object({
  label: z.string().nonempty('required'),
  key: z.string().nonempty('required'),
  domainId: z.number(),
});

export const DomainSchema = z.object({
  label: z.string().nonempty('required'),
  key: z.string().nonempty('required'),
});

export const UpdateIssueSchema = z.object({
  id: z.number().optional(),
  label: z.string().optional(),
  key: z
    .string()
    .regex(/^[^\s]+$/, 'Key cannot contain spaces')
    .optional(),
  categoryId: z.number().optional(),
  defaultTimeFrameInDays: z.coerce
    .number()
    .min(0, 'Default time frame must be at least 0')
    .optional(),
  activityIds: z.array(z.number()).optional(),
  zcodeId: z.number().optional(),
  tagCloud: z.string().optional(),
});

export const UpdateZcodeSchema = z.object({
  id: z.number().optional(),
  label: z.string().optional(),
  key: z
    .string()
    .nonempty('required')
    .regex(
      /^zcode_Z\d+\.\d+$/,
      'Key must be in format "zcode_Z##.#" (e.g., zcode_Z59.82 or zcode_Z59.4)'
    ),
  overview: z.string().nonempty('required'),
  issues: z.string().optional(),
  assistances: z.string().optional(),
});

export const UpdateActivitySchema = z.object({
  id: z.number().optional(),
  key: z.string().nonempty('required'),
  label: z.string().nonempty('required'),
  overview: z.string().nonempty('required'),
  iconName: z.string().optional().nullable(),
  defaultTimeFrameInMinutes: z.coerce.number(),
});

export const UpdateCategorySchema = z.object({
  id: z.number().optional(),
  label: z.string().nonempty('required'),
  key: z.string().nonempty('required'),
  domainId: z.number(),
});

export const UpdateDomainSchema = z.object({
  id: z.number().optional(),
  label: z.string().nonempty('required'),
  key: z.string().nonempty('required'),
});

export const CreateNeedFormSchema = z
  .object({
    tempNeedId: z.string().optional(),
    issueId: z.number().int().positive('Issue is required'),
    reason: z.string().refine(
      value => {
        // Remove HTML tags and check if empty
        const plainText = value.replace(/<[^>]*>/g, '');
        const trimmed = plainText.trim();
        const isEmptyHtml = trimmed === '';
        return !isEmptyHtml;
      },
      {
        message: 'Reason is required',
      }
    ),
  })
  .strict();

export const UpdateNeedFormSchema = z.object({
  domainId: z.number().optional(),
  categoryId: z.number().optional(),
  issueId: z.number().optional(),
  reason: z.string().optional(),
});

export type UpdateNeedFormDto = z.infer<typeof UpdateNeedFormSchema>;

export interface MetadataUpdateInputMap {
  activities: UpdateActivityRequest;
  categories: UpdateCategoryRequest;
  domains: UpdateDomainRequest;
  issues: UpdateIssueRequest;
  zcodes: UpdateZcodeRequest;
}

export interface MetadataCreateInputMap {
  activities?: CreateActivityRequest;
  categories?: CreateCategoryRequest;
  domains?: CreateDomainRequest;
  issues?: CreateIssueRequest;
  zcodes?: CreateZcodeRequest;
}
